import { useState, type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    LabelText,
    ValueCell,
    ValueText,
    WrappingValueText,
} from '~/components/Fields/LabeledValue';
import EditablePropertyValue from '~/components/Fields/EditablePropertyValue';
import NumberValue from '~/components/Fields/NumberValue';
import PropertyEditButton from '~/components/Fields/PropertyEditButton';
import PropertiesPanel from '~/components/Properties/PropertiesPanel';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import StructureColorValue from '~/components/Structures/StructureColorValue';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useStructureNameValidation from '~/hooks/structure-set/useStructureNameValidation';
import {
    formatRecordedTime,
    formatRecordedUser,
} from '~/components/Provenance/provenanceRows';
import DensityOverrideField from './DensityOverrideField';

interface StructureDetailsInformationProps {
    roi: RoiReadModel;
    /** ROI volume in cm³ (already converted at the UI boundary). */
    volumeCm3: number;
    equivalentSphereDiameterCm: number;
    /** Whether the structure's metadata may be edited (false when approved). */
    isEditable: boolean;
    onRename: (name: string) => void;
    /** Opens the Type menu beside the panel, level with `row`. */
    onEditType: (row: HTMLElement) => void;
    /** Set (number) or remove (undefined) the electron density override. */
    onElectronDensityChange: (value: number | undefined) => void;
    /** Set (number) or remove (undefined) the mass density override. */
    onMassDensityChange: (value: number | undefined) => void;
}

/**
 * The identifying and physical properties of a structure, as a label and
 * value grid: name, colour, ROI number, approval status (with who recorded
 * the structure's own review and when, once it is saved), interpreted type,
 * generation algorithm, then for a volume its volume (cm³), equivalent
 * sphere diameter (cm) and contour count, the electron and mass density
 * overrides, and the description when it has one. While the structure is
 * editable, a pencil ends the name, colour, type and density values: the
 * name and densities edit in place, the colour and type edits open beside
 * the panel. A missing density override reads "Not set", never zero.
 */
export default function StructureDetailsInformation({
    roi,
    volumeCm3,
    equivalentSphereDiameterCm,
    isEditable,
    onRename,
    onEditType,
    onElectronDensityChange,
    onMassDensityChange,
}: StructureDetailsInformationProps) {
    const intl = useIntl();
    const validateName = useStructureNameValidation(roi);
    const [isRenaming, setIsRenaming] = useState(false);
    const renameLabel = intl.formatMessage({ id: 'rename' });
    const dash = '—';

    return (
        <PropertiesPanel variant="panel">
            <LabelText>
                <FormattedMessage id="name" />
            </LabelText>
            <EditablePropertyValue
                display={
                    <ValueText
                        component="span"
                        title={roi.name}
                        data-testid="structure-properties-name-value"
                    >
                        {roi.name}
                    </ValueText>
                }
                editable={isEditable}
                editing={isRenaming}
                onEditingChange={setIsRenaming}
                seed={roi.name}
                onSubmit={onRename}
                validate={validateName}
                editLabel={renameLabel}
                fieldLabel={renameLabel}
                fontWeight={500}
                buttonTestId="structure-properties-rename-button"
            />

            <LabelText>
                <FormattedMessage id="colorPicker.colorLabel" />
            </LabelText>
            <StructureColorValue roi={roi} />

            <LabelText>
                <FormattedMessage id="structure.roiNumber" />
            </LabelText>
            <NumberValue
                value={roi.roiNumber}
                data-testid="structure-properties-roi-number-value"
            />

            <LabelText>
                <FormattedMessage id="approvalStatus" />
            </LabelText>
            <ValueText component="span" display="flex">
                <ApprovalStatusLabel
                    status={roi.approvalStatus}
                    size={14}
                    data-testid="structure-properties-approval-value"
                />
            </ValueText>

            {roi.recordedReview && (
                <>
                    <LabelText>
                        <FormattedMessage id="structure.reviewedBy" />
                    </LabelText>
                    <WrappingValueText data-testid="structure-properties-reviewed-by-value">
                        {[
                            formatRecordedUser(
                                intl,
                                roi.recordedReview.reviewer
                            ),
                            formatRecordedTime(
                                intl,
                                roi.recordedReview.reviewedAt
                            ),
                        ].join(' / ')}
                    </WrappingValueText>
                </>
            )}

            <LabelText>
                <FormattedMessage id="structure.type" />
            </LabelText>
            <ValueCell>
                <ValueText
                    component="span"
                    data-testid="structure-properties-type-value"
                >
                    {roi.interpretedType || dash}
                </ValueText>
                {isEditable && (
                    <PropertyEditButton
                        aria-label={intl.formatMessage({
                            id: 'structure.type.change',
                        })}
                        onClick={(event: MouseEvent<HTMLElement>) =>
                            onEditType(event.currentTarget)
                        }
                        data-testid="structure-properties-type-edit-button"
                    />
                )}
            </ValueCell>

            <LabelText>
                <FormattedMessage id="structure.algorithm" />
            </LabelText>
            <ValueText
                component="span"
                data-testid="structure-properties-algorithm-value"
            >
                {roi.roiGenerationAlgorithm || dash}
            </ValueText>

            {roi.modelGenerated && (
                <>
                    <LabelText>
                        <FormattedMessage id="structure.aiGenerated" />
                    </LabelText>
                    <ValueText
                        component="span"
                        data-testid="structure-properties-ai-generated-value"
                    >
                        <FormattedMessage
                            id={
                                roi.contourEditedSinceGeneration
                                    ? 'structure.aiGenerated.edited'
                                    : 'structure.aiGenerated.unedited'
                            }
                        />
                    </ValueText>
                </>
            )}

            {!roi.isPointRoi && (
                <>
                    <LabelText>
                        <FormattedMessage id="structure.volume" />
                    </LabelText>
                    <NumberValue
                        value={
                            isFinite(volumeCm3) ? volumeCm3.toFixed(2) : dash
                        }
                        unit={isFinite(volumeCm3) ? 'cm³' : undefined}
                        data-testid="structure-properties-volume-value"
                    />

                    <LabelText>
                        <FormattedMessage id="structure.eqSphereDiam" />
                    </LabelText>
                    <NumberValue
                        value={
                            isFinite(equivalentSphereDiameterCm)
                                ? equivalentSphereDiameterCm.toFixed(2)
                                : dash
                        }
                        unit={
                            isFinite(equivalentSphereDiameterCm)
                                ? 'cm'
                                : undefined
                        }
                    />

                    <LabelText>
                        <FormattedMessage id="structure.contours" />
                    </LabelText>
                    <NumberValue value={roi.contourCount} />
                </>
            )}

            <LabelText>
                <FormattedMessage id="structure.electronDensity" />
            </LabelText>
            <DensityOverrideField
                value={roi.electronDensityOverride}
                isEditable={isEditable}
                onChange={onElectronDensityChange}
                editLabelId="structure.density.edit.electron"
                fieldLabelId="structure.electronDensity"
                dataTestId="structure-electron-density-value"
            />

            <LabelText>
                <FormattedMessage id="structure.massDensity" />
            </LabelText>
            <DensityOverrideField
                value={roi.massDensityOverride}
                isEditable={isEditable}
                onChange={onMassDensityChange}
                editLabelId="structure.density.edit.mass"
                fieldLabelId="structure.massDensity"
                dataTestId="structure-mass-density-value"
            />

            {roi.roiDescription && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <WrappingValueText
                        component="span"
                        title={roi.roiDescription}
                    >
                        {roi.roiDescription}
                    </WrappingValueText>
                </>
            )}
        </PropertiesPanel>
    );
}
